import React, { useRef, useEffect } from "react";
import kiraLogo from "../../../assets/Kira.png";
import type { TranscriptItem } from "../hooks/useTranslator";
import { useUiLang } from "../../../context/UiLangContext";

interface MinutaViewProps {
  meetingTranscript: TranscriptItem[];
  subtitleOrig: string;
  subtitleTran: string;
  tgtLang: string;
  recording: boolean;
  isConnecting?: boolean;
  onOpenSummary: () => void;
  onToggleRecording: () => void;
}

export const MinutaView: React.FC<MinutaViewProps> = ({
  meetingTranscript,
  subtitleOrig,
  subtitleTran,
  tgtLang,
  recording,
  isConnecting,
  onOpenSummary,
  onToggleRecording,
}) => {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll history when new items arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [meetingTranscript.length]);

  const steps = isEn
    ? [
        <><strong className="text-[var(--text)] font-semibold">Click Start</strong> and select the system tab or window with audio.</>,
        "Kira records everything in the background, seamlessly.",
        "When stopped, minutes are generated automatically.",
      ]
    : [
        <><strong className="text-[var(--text)] font-semibold">Hacé clic en Iniciar</strong> y seleccioná la pestaña o ventana con el audio de la reunión.</>,
        "Kira registra todo en segundo plano, sin interrupciones.",
        "Al detener, la minuta se genera automáticamente.",
      ];

  const hasActiveSession = recording || isConnecting;

  return (
    <div className="flex-1 flex flex-col min-h-0 relative h-full overflow-hidden bg-[var(--bg2)]">
      {/* 1. Idle State / Before Recording */}
      {!hasActiveSession && (
        <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12 overflow-y-auto">
          <div className="max-w-[480px] w-full text-center flex flex-col items-center animate-fade-in my-auto">
            <div className="w-[220px] h-[220px] mb-5 flex items-center justify-center relative">
              <img
                src={kiraLogo}
                className="max-w-full max-h-full object-contain filter drop-shadow-[0_8px_16px_rgba(0,0,0,0.35)] animate-[gentle-float_6s_ease-in-out_infinite]"
                alt="Kira Logo"
              />
            </div>
            <p className="text-xs text-[var(--accent)] font-medium mb-6">
              {isEn ? "Automatic meeting registry" : "Registro automático de reuniones"}
            </p>

            <div className="w-full bg-[var(--bg3)] border border-[var(--b1)] py-4 px-5 rounded-[12px] text-left space-y-[11px] mb-5">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="w-[20px] h-[20px] rounded-full bg-[var(--bg4)] border border-[var(--b2)] flex items-center justify-center text-xs font-bold text-[var(--text-muted)] flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm text-[var(--text-muted)] leading-[1.55]">{step}</p>
                </div>
              ))}
            </div>

            {/* <div className="inline-flex items-center gap-1.5 border border-[var(--b1)] bg-[var(--bg3)] rounded-full px-4 py-1.5 text-xs text-[var(--text-dim)]">
              <span className="w-[6px] h-[6px] bg-[var(--text-dim)] rounded-full inline-block animate-pulse" />
              {isEn ? "Waiting for system audio..." : "Esperando audio del sistema..."}
            </div> */}
          </div>
        </div>
      )}

      {/* 2. Active Session View (Matching INTERFAZ_DE_KIRA.html Solo Minuta mode) */}
      {hasActiveSession && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
          {/* Top Session Header - Temporarily commented out to avoid overlap with floating status/history tags
          <div className="px-6 md:px-10 pt-3.5 pb-2.5 flex items-center gap-2 flex-shrink-0 border-b border-[var(--b1)] bg-[var(--bg2)]">
            <span className="text-xs font-[700] tracking-[0.08em] uppercase text-[var(--text-dim)]">
              {isEn ? "CURRENT SESSION" : "SESIÓN EN CURSO"}
            </span>
            <span className="text-xs font-[700] tracking-[0.04em] px-2 py-0.5 rounded-full bg-emerald-500/12 text-emerald-500">
              {isEn ? "Minutes Only" : "Solo Minuta"}
            </span>
          </div>
          */}

          {/* Scrollable Plain Monospace History List */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-6 md:px-10 py-5 flex flex-col gap-1.5 scroll-bg scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent font-mono"
          >
            {meetingTranscript.map((item, index) => (
              <div key={index} className="text-sm text-[var(--text)] leading-[1.7] py-0.5 animate-fade-in">
                <span className="text-sm text-[var(--text-dim)] mr-2 select-none">[{item.timestamp}]</span>
                <span className="select-text">{item.tran || item.orig}</span>
              </div>
            ))}
          </div>

          {/* Fixed Bottom Live Segment (Sober plain-text style matching .solo-mode in prototype) */}
          {(recording || isConnecting || subtitleTran || subtitleOrig) && (
            <div className="px-6 md:px-10 pt-5 pb-6 border-t border-[var(--b1)] bg-[var(--bg2)] flex items-center justify-between gap-4 flex-shrink-0 relative font-mono">
              <div
                className={`text-base sm:text-lg font-[600] leading-[1.4] tracking-[-0.01em] select-text animate-fade-in ${
                  subtitleTran || subtitleOrig ? "text-[var(--text)]" : "text-[var(--text-dim)] font-normal text-sm"
                }`}
              >
                {subtitleTran || subtitleOrig || (isConnecting ? (isEn ? "Connecting to audio..." : "Conectando al audio...") : (isEn ? "Listening in background..." : "Escuchando en segundo plano..."))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
